/**
 * Features — Landing, Códice Botánico edition.
 * Feature grid + technology strip. SVG icons (no emoji), codex palette.
 */
import React from 'react';
import { motion } from 'framer-motion';

const features = [
  {
    title: 'Escáner NOM-051',
    description: 'Soberanía alimentaria en cada elección',
    points: [
      'Lee códigos de barras automáticamente',
      'Analiza sellos de advertencia NOM-051',
      'Compara con alternativas mexicanas',
      'Prioriza productos locales y Bienestar',
    ],
    color: 'var(--cdx-turquesa)',
    Icon: ScanIcon,
  },
  {
    title: 'Plantas medicinales',
    description: 'Medicina tradicional validada por la UNAM',
    points: [
      '22 plantas con evidencia científica',
      'Métodos de preparación tradicionales',
      'Seguridad, dosis y precauciones',
      'Basado en el Atlas de la UNAM',
    ],
    color: 'var(--cdx-jade)',
    Icon: LeafIcon,
  },
  {
    title: 'Huitzi · asistente IA',
    description: 'El colibrí mensajero de tu salud',
    points: [
      'Respuestas basadas en datos curados',
      'Recomendaciones personalizadas',
      'Conversación natural en español',
      'Acceso ilimitado, sin citas',
    ],
    color: 'var(--cdx-copal)',
    Icon: ColibriIcon,
  },
  {
    title: 'Planes de alimentación',
    description: 'Producto de temporada en cada comida',
    points: [
      'Basado en el Sistema SMAE',
      '56+ alimentos locales priorizados',
      'Bases del domingo listas',
      'Recetas con ingredientes nacionales',
    ],
    color: 'var(--cdx-cempasuchil)',
    Icon: PlateIcon,
  },
  {
    title: 'Seguimiento',
    description: 'Cuida tu evolución con criterio',
    points: [
      'Registro de comidas y digestión',
      'Cobertura fitoquímica semanal',
      'Historial completo',
      'Sin culpa, solo datos',
    ],
    color: 'var(--cdx-jamaica)',
    Icon: ChartIcon,
  },
];

const tech = [
  ['Inteligencia artificial', 'Vertex AI · Gemini', SparkIcon],
  ['Estándares mexicanos', 'NOM-051 y SMAE', BadgeIcon],
  ['Seguridad', 'Datos encriptados', ShieldIcon],
  ['Rápido', 'Respuesta < 2 s', ClockIcon],
  ['Multiplataforma', 'Web · iOS · Android', DevicesIcon],
];

const Features = () => (
  <section
    className="cdx-grain relative"
    style={{ padding: '88px 0', background: 'var(--cdx-paper-2)', fontFamily: 'var(--cdx-font-body)' }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[1]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-14"
      >
        <span className="cdx-kicker">El herbario completo</span>
        <h2 className="cdx-display" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', margin: '10px 0 12px', color: 'var(--cdx-ink)' }}>
          Todo para mejorar tu nutrición
        </h2>
        <p style={{ fontSize: 18, color: 'var(--cdx-ink-soft)', maxWidth: 620, margin: '0 auto' }}>
          Herramientas serias, en español, basadas en estándares mexicanos y sabiduría ancestral.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {features.map((f, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.08, duration: 0.5 }}
            style={{
              background: 'var(--cdx-paper)',
              border: '1px solid var(--cdx-paper-edge)',
              borderRadius: 'var(--cdx-r-lg)',
              padding: '22px 20px',
              boxShadow: 'var(--cdx-shadow-sm)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              style={{
                width: 48, height: 48, borderRadius: 12, marginBottom: 16,
                background: `color-mix(in srgb, ${f.color} 16%, transparent)`,
                color: f.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <f.Icon />
            </div>
            <h3 className="cdx-display" style={{ fontSize: 18, color: 'var(--cdx-ink)', marginBottom: 6 }}>{f.title}</h3>
            <p style={{ color: 'var(--cdx-ink-soft)', fontSize: 13.5, marginBottom: 14, lineHeight: 1.45 }}>{f.description}</p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
              {f.points.map((p, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13, color: 'var(--cdx-ink-soft)' }}>
                  <Check color={f.color} /> <span>{p}</span>
                </li>
              ))}
            </ul>
            <div style={{ height: 3, width: '100%', marginTop: 16, borderRadius: 999, background: f.color, opacity: 0.7 }} />
          </motion.div>
        ))}
      </div>

      {/* Technology strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="cdx-grain"
        style={{
          marginTop: 56, position: 'relative', overflow: 'hidden',
          background: 'var(--cdx-paper)', borderRadius: 'var(--cdx-r-xl)',
          border: '1px solid var(--cdx-paper-edge)', boxShadow: 'var(--cdx-shadow-md)',
        }}
      >
        <div className="cdx-greca--gold cdx-greca" />
        <div style={{ padding: '28px 28px 32px', position: 'relative', zIndex: 1 }}>
          <h3 className="cdx-display" style={{ fontSize: 22, textAlign: 'center', color: 'var(--cdx-ink)', marginBottom: 28 }}>
            Tecnología de vanguardia al servicio de tu salud
          </h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {tech.map(([label, sub, Icon]) => (
              <div key={label} className="flex flex-col items-center text-center">
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', marginBottom: 12,
                  background: 'var(--cdx-jade-wash)', color: 'var(--cdx-jade)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon />
                </div>
                <p style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--cdx-ink)' }}>{label}</p>
                <p style={{ fontSize: 11.5, color: 'var(--cdx-ink-faint)' }}>{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---- icons (24x24, currentColor) ---- */
const Check = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill={color} aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);
function ScanIcon() {
  return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M4 7V5a1 1 0 0 1 1-1h2M17 4h2a1 1 0 0 1 1 1v2M20 17v2a1 1 0 0 1-1 1h-2M7 20H5a1 1 0 0 1-1-1v-2" strokeLinecap="round" />
    <path d="M4 12h16" strokeLinecap="round" /></svg>);
}
function LeafIcon() {
  return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M5 19c0-8 5-13 14-14 1 9-4 15-14 14Z" strokeLinejoin="round" />
    <path d="M9 15c2-3 5-5 8-6" strokeLinecap="round" /></svg>);
}
function ColibriIcon() {
  return (<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M9 16c-1-4 1-7 4-8 2-1 5 0 6 1l-3 1 3 1-2 2 2 1c-2 2-6 2-8 1" opacity="0.85" />
    <circle cx="8" cy="9" r="2.2" /><path d="M6 8L1 6l5 1z" /></svg>);
}
function PlateIcon() {
  return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.5" /></svg>);
}
function ChartIcon() {
  return (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M4 19V5M4 19h16" strokeLinecap="round" /><path d="M8 15l3-4 3 2 4-6" strokeLinecap="round" strokeLinejoin="round" /></svg>);
}
function SparkIcon() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8z" /><circle cx="18" cy="18" r="2" /></svg>);
}
function BadgeIcon() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <circle cx="12" cy="9" r="5" /><path d="M9 13l-2 8 5-3 5 3-2-8" strokeLinejoin="round" /></svg>);
}
function ShieldIcon() {
  return (<svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>);
}
function ClockIcon() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" strokeLinecap="round" /></svg>);
}
function DevicesIcon() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <rect x="3" y="5" width="13" height="9" rx="1" /><rect x="17" y="8" width="4" height="11" rx="1" /><path d="M3 18h9" strokeLinecap="round" /></svg>);
}

export default Features;
